// REACT IMPORTS
import React, { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";

// OPEN LAYER IMPORTS
import "ol/ol.css";
import Map from "ol/Map";
import View from "ol/View";
import TileLayer from "ol/layer/Tile";
import "./openLayerMap.scss";
import XYZ from "ol/source/XYZ";
import { fromLonLat, get as getProjection } from "ol/proj";
import { Fill, Stroke, Style } from "ol/style";
import { Vector as VectorLayer } from "ol/layer";
import { Vector as VectorSource } from "ol/source";
import { GeoJSON } from "ol/format";
import { isEmpty } from 'ol/extent';
import { register as registerProj4 } from 'ol/proj/proj4';
import proj4 from "proj4";
import Select from "ol/interaction/Select.js";
import { click} from "ol/events/condition.js";
import { crsTransformation } from "./crsTransformation";

// OTHER IMPORTS
import { toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import config from "../../../config"
import { replaceSubdomainPlaceholder } from "../../../helpers/gisTools/replaceSubdomainPlaceholder";
import { getComuneLayers, getComuni, getLayer } from "../../../api";
import init from "three/examples/jsm/offscreen/scene.js";
import { ProgressBar } from "../../index";
import mapStyle from "../../../helpers/gisTools/mapStyle";
import sortLayers from "../../../helpers/gisTools/sortLayers";



// CRS Definitions
//proj4.defs("EPSG:3857", "+proj=merc +lon_0=0 +k=1 +x_0=0 +y_0=0 +datum=WGS84 +units=m +no_defs");
proj4.defs("EPSG:4326", "+proj=longlat +datum=WGS84 +no_defs");
proj4.defs("EPSG:32632", "+proj=utm +zone=32 +datum=WGS84 +units=m +no_defs +type=crs");
proj4.defs( "EPSG:32633","+proj=utm +zone=33 +datum=WGS84 +units=m +no_defs +type=crs");
proj4.defs("EPSG:3857","+proj=merc +a=6378137 +b=6378137 +lat_ts=0.0 +lon_0=0.0 +x_0=0.0 +y_0=0 +k=1.0 +units=m +nadgrids=@null +wktext  +no_defs");
registerProj4(proj4);
const targetProjection = "EPSG:3857";


export const OLMap = ({
  backgroundMap, // Checked OK
  children, // ??
  className = "", // ??
  layers, // Checked OK
  mapHeight, // Checked OK
  mapViewTarget, // Checked OK
  handleFeatureProperties, // ??
  isEyeOpen, // ??
  title, // ??
  handleCallBackLayerTitle,  // ??
  setLayers, // Checked OK
}) => {
  const mapRef = useRef(null);
  const navigate = useNavigate();
  const [map, setMap] = useState(null);
  const [names, setNames] = useState([]);
  const [legendUrl, setLegendUrl] = useState(null);
  const [fetchedAllLayers, setFetchedAllLayers] = useState(false);
  const communiVectorSource = useRef(new VectorSource());
  const accessToken = localStorage.getItem("authToken");


 // ********************************************************************************
 // Fetching layers of the selected comune
 useEffect(() => {
  // if(layers.length > 0) return;
  const listLayers = async (comuneId) => {
    try {
      var data = await getComuneLayers(comuneId);
      let newLayers = data.layers;
      if (newLayers.length === 0) {
        return;
      }
      var data = sortLayers(newLayers).map((layer) => {
        const vectorSource = new VectorSource(); 
        return {
          id: layer.id,
          tipo: layer.tipo,
          chiave: layer.chiave,
          group: layer.group,
          style: layer.style,
          colors: layer.colors,
          crs: layer.crs,
          vectorSource: vectorSource,
          vectorLayer: new VectorLayer({
            source: vectorSource,
            //projection: layer.crs,
            name: layer.tipo
          }),
        };
      });
      setLayers(data);
    } catch (error) {
      if(error.response && error.response.status === 401){
        navigate("/");
        throw new Error("Your session has ended. Redirecting you to the login page...");
      } else {
        toast.error(`Error fetching layers of municipality with id=${comuneId}: ${error.message}`);
        throw new Error("Network response was not ok");
      }
    }
  };
  if(mapViewTarget?.comune) listLayers(mapViewTarget.comune.id);
}, [mapViewTarget]);

//*************************************************************** */
// Load Vector Source for each available layer
const fetchLayer = async (layer) => {
  try {
    const data = await getLayer(layer.id);
    // Check if there are features
    if (!data.features || data.features.length === 0) {
      console.warn(`Layer ${layer.id} has no features`);
      return;
    }

    
      /* DEBUGGING CODE */
      
      const sample = data.features[0];
      const geom = sample?.geometry;
      const coords = geom?.coordinates;
      console.group(`[GIS] Layer "${layer.tipo}" (id=${layer.id})`);
      console.log("layer.crs (metadata):", layer.crs);
      console.log("targetProjection (map):", targetProjection);
      console.log("vectorLayer.projection:", layer.vectorLayer?.get?.("projection") ?? layer.crs);
      console.log("geometry type:", geom?.type);
      console.log("first feature – RAW coordinates (API):", JSON.stringify(coords));
      console.groupEnd();
      // Help guess CRS from numbers
      /*
      if (geom?.type === "Point") {
      const [x, y] = coords;
      console.log("sample Point [x,y]:", x, y);
      if (Math.abs(x) <= 180 && Math.abs(y) <= 90) {
        console.log("→ looks like degrees (EPSG:4326 lon/lat range)");
      } else if (x > 100000 || y > 100000) {
        console.log("→ looks like projected meters (UTM/Web Mercator?)");
      }
      }
      */



    const format = new GeoJSON();
    const transformedFeatures = data.features.map((feature) => crsTransformation(layer.crs, targetProjection, feature));
    
    // const sampleAfter = transformedFeatures[0]?.geometry?.coordinates;
    // console.log("first feature – AFTER crsTransformation:", JSON.stringify(sampleAfter));

    const transformedGeoJSON = { type: "FeatureCollection", features: transformedFeatures};
    const transformedOlFeatures = format.readFeatures(transformedGeoJSON, { dataProjection: targetProjection, featureProjection: targetProjection});
    

    /* DEBUGGING CODE */
    /*
    const sourceProj = layer.vectorSource.getProjection()?.getCode();
    console.log("vectorSource projection:", sourceProj ?? "(default → view = EPSG:3857)");
    const olGeom = transformedOlFeatures[0]?.getGeometry();
    if (olGeom) {
      const flat = olGeom.getFlatCoordinates?.() ?? olGeom.getCoordinates();
      console.log("OL geometry extent:", olGeom.getExtent());
      console.log("OL first coords sample:", flat?.slice?.(0, 4) ?? flat);
    }
    */
    
    
    
    await layer.vectorSource.clear();
    await layer.vectorSource.addFeatures(transformedOlFeatures);

    // Use a style function instead of a static style to handle different geometry types
    const styleFunction = (feature) => {
      const geometry = feature.getGeometry();
      if (!geometry) return null;

      const geometryType = geometry.getType();
      return mapStyle(layer, geometryType, feature);
    };

    await layer.vectorLayer.setStyle(styleFunction);

  } catch (error) {
    if (error.response && error.response.status === 401) {
      navigate("/");
      throw new Error("Your session has ended. Redirecting you to the login page...");
    } else {
      toast.error(`Error fetching layer: ${error.message}`);
      throw new Error("Network response was not ok");
    }
  }
};

// ************************************************************************************
// FETCH ALL LAYERS
useEffect(() => {
  setFetchedAllLayers(false);
  async function fetchLayers() {
    try {
      await Promise.all(layers.map(layer => fetchLayer(layer)));
      setFetchedAllLayers(true);
    } catch (error) {
      console.warn("couldn't fetch layers.");
    }
  }
  fetchLayers();
}, [layers]);

  // ********************************************************************************
  // BUILD THE MAP
  useEffect(() => {
    if(fetchedAllLayers === false) return;
    async function initializeMap() {
      const center = [0, 0];
      let mapLayers = [
        new TileLayer({
          source: new XYZ({
            url: replaceSubdomainPlaceholder({url: backgroundMap.url, subdomains: backgroundMap.subdomains }),
            attributions: '...',
            maxZoom: backgroundMap.maxZoom ? backgroundMap.maxZoom : 19,
            crossOrigin: 'anonymous',
          }),
        })
      ]
      /*
      try {
        for (const layer of layers) await fetchLayer(layer);
      } catch (error) {
        console.warn("couldn't fetch layers.");
      }
      */
      layers.forEach((layer) => {
        mapLayers.push(layer.vectorLayer);
      });
    
      const mapInstance = new Map({
        target: mapRef.current ?? "",
        layers: mapLayers,
        view: new View({
          center: fromLonLat(center, targetProjection),
          zoom: 2,
          projection: getProjection(targetProjection)  
        }),
      });
      // Zoom to the extent of the selected feature
      const targetLayerType =  mapViewTarget?.layer ? mapViewTarget.layer : "limiti";
      if (true || mapLayers.length > 1) {
        const targetLayer = layers.find((layer) => layer.tipo.toLowerCase() === targetLayerType.toLowerCase());
        if(!targetLayer) return;

        // Wait for features to be loaded in the vectorSource
        const waitForFeatures = async () => {
          const maxRetries = 10;
          const retryDelay = 100; // milliseconds
          
          for (let i = 0; i < maxRetries; i++) {
            const features = targetLayer.vectorSource.getFeatures();
            if (features.length > 0) {
              return features;
            }
            // Wait a bit before retrying
            await new Promise(resolve => setTimeout(resolve, retryDelay));
          }
          return targetLayer.vectorSource.getFeatures(); // Return even if empty after retries
        };
        
        const allFeatures = await waitForFeatures();


        let targetFeature = null;
        if (mapViewTarget.key && mapViewTarget.value) {
          targetFeature = targetLayer.vectorSource.getFeatures().find((feature) => feature.get(mapViewTarget.key) === mapViewTarget.value);
        } 

        const source = targetLayer.vectorSource;
        let extent = null;
        if (targetFeature) {
          const geometry = targetFeature.getGeometry();
          const geometryType = geometry.getType();

          // If the target feature is a Point, create a buffer extent around it
          if (geometryType === 'Point') {
            
            const coordinate = geometry.getCoordinates();
            // Buffer size in meters (adjust this value to change the zoom level)
            // 2000 meters = ~2km radius, adjust as needed
            const bufferSize = 2000;
            extent = [
              coordinate[0] - bufferSize,
              coordinate[1] - bufferSize,
              coordinate[0] + bufferSize,
              coordinate[1] + bufferSize
            ];
          } else {
            // For non-Point geometries, use the existing extent logic
            extent = geometry.getExtent();
          }
        } else {
          extent = source.getExtent();
        }
        if (extent && !isEmpty(extent)) {
          // For Point features, use padding and maxZoom for better control
          if (false && targetFeature && targetFeature.getGeometry().getType() === 'Point') {
            mapInstance.getView().fit(extent, { 
              duration: 1000,
              // padding: [50, 50, 50, 50], // padding in pixels
              maxZoom: 15 // maximum zoom level (adjust as needed: 10=far, 15=medium, 18=close)
            });
          } else {
            mapInstance.getView().fit(extent, { duration: 1000 });
          }
        } else {
          console.warn("The extent of the target layer is empty.", mapLayers[1]);
        }
      }
      setMap(mapInstance);

      // getting feature property
      const handleMapClick = (evt) => {
        const feature = mapInstance.forEachFeatureAtPixel(
           evt.pixel,
          (feature) => feature
        );
        if (feature) {
          const props = feature.getProperties();
          const layer = mapInstance.getLayerGroup().getLayers().getArray().find(l => {
             const source = l.getSource()
             return source instanceof VectorSource && source.getFeatures().includes(feature);
          });
          const layerProps = layer ? layer.getProperties() : {};
          handleFeatureProperties({ ...props, layerName: layerProps.name });
          }  else { 
          handleFeatureProperties({});
        }
      };

      // getting layer property and condition based map Interactions
      const handleLayerClick = (evt) => {
        let clickedFeature = null;

        mapInstance.getInteractions().forEach((interaction) => {
          if (interaction instanceof Select) {
            mapInstance.removeInteraction(interaction);
          }
        });
        mapInstance.forEachFeatureAtPixel(evt.pixel, (feature, layer) => {
          if (feature && !clickedFeature) {
            clickedFeature = feature;
            const layerTitle = layer.get("title");
            handleCallBackLayerTitle(layerTitle);

            const selected = new Style({
              fill: new Fill({
                color: "#00FFFF",
              }),
              stroke: new Stroke({
                color: "rgba(255, 255, 255, 0.7)",
                width: 5,
              }),
            });

            // Check if layerTitle matches the title prop
            if (
              layerTitle
            ) {
              function selectStyle(feature) {
                const color = feature.get("COLOR") || "#00FFFF";
                selected.getFill().setColor(color);
                return selected;
              }

              const selectClick = new Select({
                condition: click,
                style: selectStyle,
              });
              mapInstance.on("click", handleMapClick);
              mapInstance.addInteraction(selectClick);
            } else {
              // Remove the event listener if not needed
              mapInstance.on("click", handleMapClick);
            }
          }
        });
      };

      mapInstance.on("click", handleLayerClick);

      communiVectorSource.current.on("addfeature", () => {
        const extent = communiVectorSource.current.getExtent();
        mapInstance.getView().fit(extent, { duration: 1000 });
      });
    };
    

    initializeMap();

    return () => {
      if (mapRef.current) {
        mapRef.current.innerHTML = "";
      }
    };

    // eslint-disable-next-line
  }, [backgroundMap, fetchedAllLayers, mapViewTarget]);


  console.log(">>> MAP VIEW TARGET (OPEN LAYER MAP): ", mapViewTarget);

  return (
    <div className="open-layer-map-main-container">
      <ProgressBar isAnimating={!fetchedAllLayers} />
      <div
        className={`open-layer-map-sub-container ${className}`}
        style={mapHeight ? { height: mapHeight } : undefined}
        ref={mapRef}
      ></div>

      {children}
    </div>
  );
};
